// Unpublished Work © 2023-2024 Deere & Company.

import {
  DeprecatedDropdown,
  DeprecatedDropdownButton,
  DeprecatedDropdownItem,
  DeprecatedDropdownMenu,
} from "@deere/fuel-react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { BusinessAction } from "../../../../api/rbac/types";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import "./orderMenu.scss";
import {
  CATEGORIES,
  TOGGLES,
} from "../../../../pages/featureToggles/constants";

const OrderCreationMenu = ({
  isEmergencyOrderEnabled,
  isStockOrderEnabled,
  isPromotionOrderEnabled,
}: {
  isEmergencyOrderEnabled: boolean;
  isStockOrderEnabled: boolean;
  isPromotionOrderEnabled: boolean;
}) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const { selectedAccounts } = useContext(DealerAccountContext);
  const { ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE } =
    useContext(ConfigContext);
  const isMultipleAccountSelected = selectedAccounts?.length > 1;

  const getOrderCreationUrl = (
    orderType: "emergency" | "stock" | "promotion"
  ) => {
    if (ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE && isMultipleAccountSelected) {
      return `/orders/${orderType}/select-account`;
    }

    return `/orders/create/${orderType}`;
  };

  const dropDownItemStyle = {
    borderWidth: "0px 0px 0px 3px",
    borderStyle: "solid",
    borderColor: "transparent",
  };
  const dropDownHoverStyle = {
    ".MenuItem-link:hover": {
      color: theme.tokens.FuelColorBrandPrimary,
      backgroundColor: theme.tokens.FuelColorAlertSuccessBackground,
      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
    },
  };
  return (
    <Box sx={dropDownHoverStyle}>
      <Typography
        data-testid="order-header-title"
        sx={{
          fontWeight: theme.tokens.FuelFontWeightSemibold,
          padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing4}`,
          fontSize: theme.tokens.FuelFontSizeMd,
          color: theme.tokens.FuelColorGray700,
        }}
      >
        {t("header.navItem.orders.createOrder")}
      </Typography>

      <Link
        to={getOrderCreationUrl("emergency")}
        style={{
          textDecoration: "none",
        }}
        hidden={!isEmergencyOrderEnabled}
      >
        <DeprecatedDropdownItem
          data-testid="order-type-emergency"
          style={dropDownItemStyle}
        >
          {t("header.navItem.orders.createOrder.emergencyOrder")}
        </DeprecatedDropdownItem>
      </Link>

      <Link
        to={getOrderCreationUrl("stock")}
        style={{
          textDecoration: "none",
        }}
        hidden={!isStockOrderEnabled}
      >
        <DeprecatedDropdownItem
          data-testid="order-type-stock"
          style={dropDownItemStyle}
        >
          {t("header.navItem.orders.createOrder.stockOrder")}
        </DeprecatedDropdownItem>
      </Link>

      <Link
        to={getOrderCreationUrl("promotion")}
        style={{
          textDecoration: "none",
        }}
        hidden={!isPromotionOrderEnabled}
      >
        <DeprecatedDropdownItem
          data-testid="order-type-promotion"
          style={dropDownItemStyle}
        >
          {t("header.navItem.orders.createOrder.promotionOrder")}
        </DeprecatedDropdownItem>
      </Link>
    </Box>
  );
};

const OrderSearchMenu = () => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const allOrdersURL = `/orders`;
  const ordersByLineItemsURL = `/orders/line-items`;
  const dropDownItemStyle = {
    borderWidth: "0px 0px 0px 3px",
    borderStyle: "solid",
    borderColor: "transparent",
  };
  const dropDownHoverStyle = {
    ".MenuItem-link:hover": {
      color: theme.tokens.FuelColorBrandPrimary,
      backgroundColor: theme.tokens.FuelColorAlertSuccessBackground,
      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
    },
  };
  return (
    <Box sx={dropDownHoverStyle}>
      <Typography
        sx={{
          fontWeight: theme.tokens.FuelFontWeightSemibold,
          padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing4}`,
          fontSize: theme.tokens.FuelFontSizeMd,
          color: theme.tokens.FuelColorGray700,
        }}
      >
        {t("header.navItem.orders.searchOrder")}
      </Typography>
      <Link
        to={allOrdersURL}
        style={{
          textDecoration: "none",
        }}
      >
        <DeprecatedDropdownItem style={dropDownItemStyle}>
          {t("header.navItem.orders.searchOrder.allOrders")}
        </DeprecatedDropdownItem>
      </Link>
      <Link
        to={ordersByLineItemsURL}
        style={{
          textDecoration: "none",
        }}
      >
        <DeprecatedDropdownItem style={dropDownItemStyle}>
          {t("header.navItem.orders.searchOrders.ordersByLineItem")}
        </DeprecatedDropdownItem>
      </Link>
    </Box>
  );
};

export const OrderMenu = ({
  dealerAccountId,
  selected = false,
}: {
  dealerAccountId: string;
  selected?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const user = useContext(UserContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const { selectedAccounts } = useContext(DealerAccountContext);

  const isAuthorizedToViewOrders =
    !isRBACEnabled || user?.isAuthorizedTo(BusinessAction.VIEW_ORDERS);
  const isAuthorizedToCreateAndEditOrders =
    !isRBACEnabled ||
    user?.isAuthorizedTo(BusinessAction.CREATE_ORDER) ||
    user?.isAuthorizedTo(BusinessAction.EDIT_ORDER);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const {
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE,
    ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE,
  } = useContext(ConfigContext);
  const isMultipleAccountSelected = selectedAccounts?.length > 1;

  const disableEmergencyOrderFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.DISABLE_EMERGENCY_ORDER);

  const disableStockOrderFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.DISABLE_STOCK_ORDER);

  const enablePromotionOrderTypeFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.ENABLE_PROMOTION_ORDER) &&
    ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE;
  const isStockOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? isMultipleAccountSelected || !disableStockOrderFeatureToggle
    : !disableStockOrderFeatureToggle;

  const isEmergencyOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? isMultipleAccountSelected || !disableEmergencyOrderFeatureToggle
    : !disableEmergencyOrderFeatureToggle;

  const isPromotionOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? (isMultipleAccountSelected && enablePromotionOrderTypeFeatureToggle) ||
      enablePromotionOrderTypeFeatureToggle
    : enablePromotionOrderTypeFeatureToggle;

  return (
    <DeprecatedDropdown>
      <DeprecatedDropdownButton
        variant="text"
        data-testid="orders-drop-down"
        style={{
          padding: "20px 8px",
          color: theme.tokens.FuelColorGray600,
          borderRadius: "0px",
          ...(selected && {
            color: theme.tokens.FuelColorBrandPrimary,
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            borderBottom: `3px solid ${theme.tokens.FuelColorBrandPrimary}`,
          }),
        }}
      >
        {t("header.navItem.orders")}
      </DeprecatedDropdownButton>
      <DeprecatedDropdownMenu
        className="order-menu-dropdown"
        style={{ minWidth: "max-content" }}
      >
        <Box sx={{ display: "flex" }}>
          {isAuthorizedToCreateAndEditOrders &&
            (isStockOrderEnabled ||
              isEmergencyOrderEnabled ||
              isPromotionOrderEnabled) && (
              <OrderCreationMenu
                dealerAccountId={dealerAccountId}
                isEmergencyOrderEnabled={isEmergencyOrderEnabled}
                isStockOrderEnabled={isStockOrderEnabled}
                isPromotionOrderEnabled={isPromotionOrderEnabled}
              />
            )}
          {isAuthorizedToViewOrders && (
            <OrderSearchMenu dealerAccountId={dealerAccountId} />
          )}
        </Box>
      </DeprecatedDropdownMenu>
    </DeprecatedDropdown>
  );
};
